import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import { REPORT_STAGES } from 'components/Erp/IntegrationStages';
import React from 'react';
import { Truck, LockKeyhole, Building2, BarChart3, ArrowDownToLine, CircleCheck, RefreshCw } from 'lucide-react';
import BlockedDriversTable from './BlockedDriversTable/BlockedDriversTable.tsx';
import { SectionHeading, EmptyState } from 'components/Erp/Presentation';
import styles from '../ReportCard.module.scss';

const AnalyticsSection = ({
  statsLoading, statsData, canReadBlocked = false,
  blockedDrivers, blockedDriversLoading, blockedDriversError,
  blockedDriversLoaded = false,
  blockedDriversPage, blockedDriversDataPage = blockedDriversPage, setBlockedDriversPage, blockedDriversCount, blockedPageSize,
}) => {
  useInterfaceLanguage();
  const initialStatsLoading = statsLoading && statsData == null;
  const hasBlockedData = blockedDriversLoaded || blockedDrivers?.length > 0;
  const initialBlockedLoading = blockedDriversLoading && !hasBlockedData;
  const inLoading = statsData?.in_loading_count || 0;
  const inRoute   = statsData?.in_route_count   || 0;
  const completed = statsData?.completed_count  || 0;
  const total     = REPORT_STAGES.reduce((sum, stage) => sum + Number(statsData?.[stage.api + '_count'] || 0), 0);
  const blocked   = blockedDriversCount || 0;

  return (
    <div className={styles.analyticsSection}>
      {/* Header */}
      {/* <div className={styles.analyticsHeader}>
        <span className={styles.analyticsIcon}></span>
        <div>
          <h2>Аналитика перевозок</h2>
          <p>Сводная статистика транспортной системы</p>
        </div>
      </div> */}

      {/* KPI strip */}
      <div className={styles.kpiStrip}>
        {REPORT_STAGES.slice(0, 3).map(stage => <div key={stage.key} className={styles.kpiCard}><div className={styles.kpiValue} style={{ color: stage.color }}>{statsData == null ? '—' : statsData?.[stage.api + '_count'] || 0}</div><div className={styles.kpiLabel}>{tr(stage.label)}</div><div className={styles.kpiAccent} style={{ background: stage.color }} /></div>)}
        <div className={styles.kpiCard}>
          <div className={styles.kpiValue}>{statsData == null ? '—' : total}</div>
          <div className={styles.kpiLabel} style={{ display: 'flex', alignItems: 'center', gap: 6 }}><BarChart3 size={14} style={{ color: "var(--theme-primary-text, #0d00f3)", flexShrink: 0 }} aria-hidden="true" />{tr("Всего перевозок")}</div>
          <div className={`${styles.kpiAccent} ${styles.kpiBlue}`}></div>
        </div>
        <div className={styles.kpiCard}>
          <div className={`${styles.kpiValue} ${styles.kpiAmber}`}>{statsData == null ? '—' : inLoading}</div>
          <div className={styles.kpiLabel} style={{ display: 'flex', alignItems: 'center', gap: 6 }}><ArrowDownToLine size={14} style={{ color: "var(--theme-warning-text, #aa7c28)", flexShrink: 0 }} aria-hidden="true" />{tr("В погрузке")}</div>
          <div className={`${styles.kpiAccent} ${styles.kpiAmberBg}`}></div>
        </div>
        <div className={styles.kpiCard}>
          <div className={`${styles.kpiValue} ${styles.kpiBlueTxt}`}>{statsData == null ? '—' : inRoute}</div>
          <div className={styles.kpiLabel} style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Truck size={14} style={{ color: "var(--theme-info-text, #3f7eac)", flexShrink: 0 }} aria-hidden="true" />{tr("В пути")}</div>
          <div className={`${styles.kpiAccent} ${styles.kpiBlue}`}></div>
        </div>
        <div className={styles.kpiCard}>
          <div className={`${styles.kpiValue} ${styles.kpiGreen}`}>{statsData == null ? '—' : completed}</div>
          <div className={styles.kpiLabel} style={{ display: 'flex', alignItems: 'center', gap: 6 }}><CircleCheck size={14} style={{ color: "var(--theme-success-text, #238268)", flexShrink: 0 }} aria-hidden="true" />{tr("Завершили")}</div>
          <div className={`${styles.kpiAccent} ${styles.kpiGreenBg}`}></div>
        </div>
        {tr(canReadBlocked && <div className={styles.kpiCard}>
          <div className={`${styles.kpiValue} ${styles.kpiRed}`}>{hasBlockedData ? blocked : '—'}</div>
          <div className={styles.kpiLabel} style={{ display: 'flex', alignItems: 'center', gap: 6 }}><LockKeyhole size={14} style={{ color: "var(--theme-danger-text, #a65473)", flexShrink: 0 }} aria-hidden="true" />{tr("Заблокировано")}</div>
          <div className={`${styles.kpiAccent} ${styles.kpiRedBg}`}></div>
        </div>)}
      </div>

      {/* Main grid */}
      <div className={styles.analyticsGrid}>
        <div className={styles.analyticsCard}>
          <div style={{ padding: '12px 16px 0' }}><SectionHeading icon={Truck} title={tr("Стадии перевозок")} tone="blue" as="h3" /></div>
          <div className={styles.chartContainer}>
            {initialStatsLoading
              ? <div className={styles.noData} role="status"><RefreshCw size={17} aria-hidden="true" />{tr("Загрузка...")}</div>
              : <DriverStatusChart statsData={statsData} />}
          </div>
        </div>



        <div className={styles.analyticsCard}>
          <div style={{ padding: '12px 16px 0' }}><SectionHeading icon={Building2} title={tr("По базам")} tone="teal" as="h3" /></div>
          <div className={styles.chartContainer}>
            {initialStatsLoading
              ? <div className={styles.noData} role="status"><RefreshCw size={17} aria-hidden="true" />{tr("Загрузка...")}</div>
              : <BaseStatsChart statsData={statsData} />}
          </div>
        </div>
        {tr(canReadBlocked && <div className={`${styles.analyticsCard} ${styles.analyticsBlockedCard}`}>
          <div style={{ padding: '12px 16px 0' }}><SectionHeading icon={LockKeyhole} title={tr("Заблокированный транспорт")} tone="rose" as="h3" actions={blocked > 0 && <span className={styles.blockedBadge}>{tr(blocked)}</span>} /></div>
          <div className={styles.analyticsBlockedContent}>
            {tr(initialBlockedLoading && <div className={styles.noData} role="status"><RefreshCw size={17} aria-hidden="true" />{tr("Загрузка...")}</div>)}
            {tr(blockedDriversError && <div className={styles.noData} role="alert" style={{ color: "var(--theme-danger-text, #f87171)" }}>{tr(blockedDriversError)}</div>)}
            {tr((hasBlockedData || (!initialBlockedLoading && !blockedDriversError)) && (
              <BlockedDriversTable
                drivers={blockedDrivers}
                page={blockedDriversDataPage}
                setPage={setBlockedDriversPage}
                count={blockedDriversCount}
                pageSize={blockedPageSize || 10}
              />
            ))}
          </div>
        </div>)}
      </div>
    </div>
  );
};

const StageDonut = ({ items }) => {
  useInterfaceLanguage();
  const total = items.reduce((sum, item) => sum + item.value, 0);
  if (!total) return <EmptyState icon={Truck} title={tr("Нет перевозок для диаграммы")} compact />;
  let offset = 0;
  return <><svg viewBox="0 0 200 200" className={styles.pieChart} role="img" aria-label={tr("Распределение перевозок по этапам")}><circle cx="100" cy="100" r="80" fill="none" stroke="var(--theme-border, #e9ecef)" strokeWidth="36" />{items.map(item => { const arc = item.value / total * 502.6; const start = offset; offset += arc; return <circle key={item.key} cx="100" cy="100" r="80" fill="none" stroke={item.color} strokeWidth="36" strokeDasharray={`${arc} 502.6`} strokeDashoffset={-start} transform="rotate(-90 100 100)" />; })}<text x="100" y="94" textAnchor="middle" fontSize="34" fontWeight="800" fill="var(--theme-text, #3a3541)">{tr(total)}</text><text x="100" y="114" textAnchor="middle" fontSize="11" fill="var(--theme-text-muted, #7c8291)">{tr("ВСЕГО")}</text></svg><div className={styles.chartLegend}>{items.map(item => <div key={item.key} className={styles.legendItem}><span className={styles.legendColor} style={{ background: item.color }} /><span className={styles.legendLabel}>{tr(item.label)}</span><span className={styles.legendValue}>{tr(item.value)}</span></div>)}</div></>;
};

const DriverStatusChart = ({ statsData }) => { useInterfaceLanguage(); return (<StageDonut items={REPORT_STAGES.map(stage => ({ ...stage, value: Number(statsData?.[stage.api + '_count'] || 0) }))} />); };

const BaseStatsChart = ({ statsData }) => {
  useInterfaceLanguage();
  const bases = statsData?.base_stats;
  if (!bases) return <EmptyState icon={Building2} title={tr("Нет перевозок по базам")} compact />;
  const value = (base, stage) => Number(base[stage.api] ?? base[({route:'loaded',completed:'delivered'})[stage.key]] ?? 0);
  return <><StageDonut items={REPORT_STAGES.map(stage => ({ ...stage, value: Object.values(bases).reduce((sum, base) => sum + value(base, stage), 0) }))} /><div className={styles.basesList}>{Object.entries(bases).map(([name, base]) => <div key={name} className={styles.baseItem}><div className={styles.baseName}>{tr(name)}</div><div className={styles.baseStatsRow}>{REPORT_STAGES.map(stage => <span key={stage.key} className={styles.statBadge} style={{ background: "var(--theme-surface, #fafafe)", color: stage.color, border: '1px solid ' + stage.color }}>{value(base,stage)} {stage.label.toLowerCase()}</span>)}</div></div>)}</div></>;
};

export default React.memo(AnalyticsSection);
